import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroParticles from './HeroParticles';

const headlines = [
  'Performance sem limite.',
  'Liderança com presença.',
  'Vida no próximo nível.',
];

export default function Hero() {
  const [currentHeadline, setCurrentHeadline] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setCurrentHeadline((prev) => (prev + 1) % headlines.length);
        setFading(false);
      }, 400);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const abrirWidget = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/813d6fa386b561dfd8b653ea7cdab408.jpg"
          alt="Carta ao Eu do Futuro — Bia Holmes"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-950/85 via-charcoal-950/40 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/50 via-transparent to-transparent"></div>
      </div>

      {/* Linha dourada topo */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/40 to-transparent"></div>

      {/* Partículas douradas */}
      <HeroParticles />

      {/* Conteúdo */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-16 pt-48 pb-32">
        <div className="max-w-3xl">
          {/* Tagline bilíngue */}
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="h-px w-8 bg-gold-400/60"></div>
            <span className="text-gold-400 text-xs font-semibold tracking-[0.3em] uppercase font-['Montserrat']">
              Método Bia Holmes · Mental Performance
            </span>
          </div>

          {/* Headline rotativa */}
          <h1 className="font-display text-6xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-4">
            <span
              className={`block text-gold-400 transition-all duration-400 ${
                fading ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
              }`}
            >
              {headlines[currentHeadline]}
            </span>
            <span className="block text-white mt-2">
              A mente certa<br />para o próximo nível.
            </span>
          </h1>

          {/* Subtítulo */}
          <p className="text-white/65 text-lg md:text-xl font-light leading-relaxed mt-8 mb-4 max-w-xl font-['Montserrat']">
            Reprogramação mental de alta performance para mulheres executivas, empresárias e líderes que já conquistaram muito — e sabem que o próximo salto é interno.
          </p>

          {/* Social proof inline */}
          <div className="flex flex-wrap items-center gap-5 mb-10">
            <div className="flex -space-x-2">
              {[
                'https://readdy.ai/api/search-image?query=professional%20executive%20woman%2040s%20confident%20smile%20minimal%20portrait%20high%20quality&width=40&height=40&seq=av-exec-01&orientation=squarish',
                'https://readdy.ai/api/search-image?query=successful%20businesswoman%2038%20dark%20hair%20elegant%20minimal%20portrait&width=40&height=40&seq=av-exec-02&orientation=squarish',
                'https://readdy.ai/api/search-image?query=confident%20mature%20woman%20executive%2045%20professional%20portrait&width=40&height=40&seq=av-exec-03&orientation=squarish',
                'https://readdy.ai/api/search-image?query=elegant%20brazilian%20woman%20professional%2035%20smiling%20portrait&width=40&height=40&seq=av-exec-04&orientation=squarish',
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt="Membro"
                  className="w-9 h-9 rounded-full border-2 border-charcoal-900 object-cover"
                />
              ))}
            </div>
            <div>
              <div className="flex gap-0.5 mb-0.5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold-400 text-xs"></i>
                ))}
              </div>
              <p className="text-white/50 text-xs font-['Montserrat']">
                +10.000 mulheres transformadas
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/quiz"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-bold tracking-wide hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] text-sm"
            >
              <i className="ri-brain-line text-base"></i>
              Fazer Meu Diagnóstico Gratuito
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
            <Link
              to="/exercicios-gratis"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white/8 border border-white/20 text-white rounded-full font-semibold hover:bg-white/14 transition-all whitespace-nowrap font-['Montserrat'] text-sm"
            >
              <i className="ri-sparkling-2-line text-gold-400 text-base"></i>
              Experimentar Técnicas Gratuitas
            </Link>
            <button
              onClick={abrirWidget}
              className="inline-flex items-center gap-2.5 px-7 py-4 bg-white/5 border border-gold-500/25 text-white/80 hover:text-white hover:bg-gold-500/10 hover:border-gold-500/50 rounded-full font-semibold transition-all whitespace-nowrap font-['Montserrat'] text-sm cursor-pointer"
            >
              <i className="ri-customer-service-2-line text-gold-400 text-base"></i>
              Falar com a Bia
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-6 mt-10">
            {[
              { icon: 'ri-shield-check-line', text: 'Garantia de 7 dias' },
              { icon: 'ri-lock-line', text: 'Pagamento seguro' },
              { icon: 'ri-award-line', text: 'Baseado em PNL clínica' },
            ].map((b) => (
              <span key={b.text} className="flex items-center gap-1.5 text-white/35 text-xs font-['Montserrat']">
                <i className={`${b.icon} text-gold-500/60`}></i>
                {b.text}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Stats - canto inferior */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-16 pb-10">
          <div className="flex flex-wrap gap-10">
            {[
              { value: '10k+', label: 'Mulheres transformadas' },
              { value: '4.9★', label: 'Avaliação média' },
              { value: '35+', label: 'Idade média das alunas' },
              { value: '8 sem.', label: 'Para resultado profundo' },
            ].map((s) => (
              <div key={s.label} className="text-left">
                <div className="font-display text-2xl font-bold text-gold-400">{s.value}</div>
                <div className="text-white/40 text-xs font-['Montserrat'] tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
